import React from "react";
import { ArrowRight, Compass, HeartStraight, HandHeart, Sparkle, CheckCircle } from "@phosphor-icons/react";
import { BrowserFrame } from "../ProductMock";

/* Atikxzio CMH visuals, in the CMH product palette (deep teal / teal / clay).
   CMH_HERO_SHOT is the career-journey artwork from the product site.
   Set CMH_INTENT_SHOT to a real "What brings you here today?" screenshot to swap the replica. */
export const CMH_HERO_SHOT = "/screens/cmh-hero.webp";
export const CMH_INTENT_SHOT = null;

const T = {
  en: {
    nav: "Atikxzio CMH", start: "Start free check-in",
    head: "Confused about your career?",
    sub: "Answer a few questions. Get a clear next step.",
    road: ["Where you are", "What fits you", "Your roadmap", "Ongoing support"],
    pills: ["Career guidance", "Mental well-being"],
    welcome: "Welcome to Atikxzio CMH",
    intentHead: "What brings you",
    intentHeadAccent: "here today?",
    intentSub: "Pick what feels closest — you can always switch later.",
    intents: [
      { icon: Compass, t: "I need help making an important career decision.", d: "Layoffs, exam prep, MBA, switch, restart, direction." },
      { icon: HeartStraight, t: "I'm struggling emotionally and want to talk to someone.", d: "Anxiety, stress, sleep, relationships, life transitions." },
      { icon: HandHeart, t: "I'm a professional who wants to mentor or counsel others.", d: "Mentors, educators, coaches and licensed counsellors." },
    ],
    footNote: "Not sure? Try the free AI check-in.",
    checkIn: "Free AI Check-in",
  },
  hi: {
    nav: "Atikxzio CMH", start: "फ्री चेक-इन शुरू करें",
    head: "करियर को लेकर उलझन है?",
    sub: "कुछ सवालों के जवाब दीजिए। अगला स्पष्ट क़दम पाइए।",
    road: ["आप कहाँ हैं", "आपके लिए क्या सही", "आपका रोडमैप", "निरंतर सहयोग"],
    pills: ["करियर मार्गदर्शन", "मानसिक स्वास्थ्य"],
    welcome: "Atikxzio CMH में स्वागत है",
    intentHead: "आज आप किस वजह से",
    intentHeadAccent: "यहाँ आए हैं?",
    intentSub: "जो सबसे क़रीब लगे वह चुनिए — बाद में बदल सकते हैं।",
    intents: [
      { icon: Compass, t: "मुझे एक अहम करियर फ़ैसले में मदद चाहिए।", d: "छँटनी, परीक्षा, MBA, स्विच, दोबारा शुरुआत, दिशा।" },
      { icon: HeartStraight, t: "मैं भावनात्मक रूप से जूझ रहा/रही हूँ और बात करना चाहता/चाहती हूँ।", d: "चिंता, तनाव, नींद, रिश्ते, जीवन के बदलाव।" },
      { icon: HandHeart, t: "मैं एक पेशेवर हूँ और दूसरों का मार्गदर्शन करना चाहता/चाहती हूँ।", d: "मेंटर, शिक्षक, कोच और लाइसेंस प्राप्त काउंसलर।" },
    ],
    footNote: "तय नहीं कर पा रहे? फ्री AI चेक-इन आज़माइए।",
    checkIn: "फ्री AI चेक-इन",
  },
};

const HeroReplica = ({ lang }) => {
  const t = T[lang];
  return (
    <div className="w-full rounded-xl bg-[#F8F6F3] p-4">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 font-display text-[11px] font-bold text-[#1D4B57]">
          <span className="flex h-5 w-5 items-center justify-center rounded-md bg-[#1D4B57] text-[9px] font-black text-white">A</span>
          {t.nav}
        </span>
        <span className="rounded-full bg-gradient-to-br from-[#1F5F5B] to-[#1D4B57] px-2.5 py-1 text-[9px] font-bold text-white">{t.start}</span>
      </div>

      <p className="mt-5 font-display text-base font-bold leading-tight text-[#1D4B57]">{t.head}</p>
      <p className="mt-1.5 text-[11px] text-[#4A5A60]">{t.sub}</p>

      <div className="mt-4 flex gap-1.5">
        {t.pills.map((p, i) => (
          <span key={p} className={`rounded-full px-2.5 py-1 text-[9px] font-bold ${i === 0 ? "bg-[#E3EEEC] text-[#1D4B57]" : "bg-[#F6E7DF] text-[#A35A3E]"}`}>{p}</span>
        ))}
      </div>

      <div className="relative mt-5 rounded-lg border border-[#E6E1DA] bg-white p-4">
        <svg viewBox="0 0 320 70" className="w-full" aria-hidden>
          <path d="M6 58 C 70 58, 70 16, 130 16 S 210 58, 250 36 S 300 14, 314 14" fill="none" stroke="#2E7D74" strokeOpacity="0.3" strokeWidth="3" strokeDasharray="7 6" strokeLinecap="round" />
          {[[6, 58], [130, 16], [250, 36], [314, 14]].map(([cx, cy], i) => (
            <circle key={i} cx={cx} cy={cy} r={i === 3 ? 7 : 5} fill={i === 3 ? "#C97B5E" : "#2E7D74"} />
          ))}
        </svg>
        <div className="mt-2 grid grid-cols-4 gap-1">
          {t.road.map((r, i) => (
            <span key={r} className={`text-[8px] font-bold leading-tight ${i === 3 ? "text-[#A35A3E]" : "text-[#1D4B57]/70"}`}>{r}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

/* "What brings you here today?" welcome step, in the CMH palette. */
const IntentReplica = ({ lang }) => {
  const t = T[lang];
  return (
    <div className="w-full rounded-xl bg-white p-3.5">
      <span className="inline-flex items-center gap-1 rounded-full border border-[#E6E1DA] px-2 py-0.5 text-[8px] font-bold uppercase tracking-[0.14em] text-[#2E7D74]">
        <Sparkle size={9} weight="fill" className="text-[#C97B5E]" /> {t.welcome}
      </span>
      <p className="mt-2.5 font-display text-[13px] font-bold leading-tight text-[#1D4B57]">
        {t.intentHead} <span className="italic text-[#2E7D74]">{t.intentHeadAccent}</span>
      </p>
      <p className="mt-1 text-[9px] leading-snug text-[#4A5A60]">{t.intentSub}</p>
      <div className="mt-3 space-y-2">
        {t.intents.map((o, i) => (
          <div key={o.t} className={`flex items-start gap-2.5 rounded-xl border bg-white px-2.5 py-2 ${i === 0 ? "border-[#2E7D74]/45" : "border-[#E6E1DA]"}`}>
            <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${i === 1 ? "bg-[#F6E7DF] text-[#C97B5E]" : "bg-[#E3EEEC] text-[#1D4B57]"}`}>
              <o.icon size={15} weight="duotone" />
            </span>
            <span className="min-w-0">
              <span className="block text-[9.5px] font-bold leading-snug text-[#1D4B57]">{o.t}</span>
              <span className="mt-0.5 block text-[8.5px] leading-snug text-[#4A5A60]">{o.d}</span>
            </span>
            {i === 0 && <CheckCircle size={13} weight="fill" className="mt-0.5 shrink-0 text-[#2E7D74]" />}
          </div>
        ))}
      </div>
      <div className="mt-3 border-t border-[#E6E1DA] pt-2.5">
        <p className="text-[8px] text-[#4A5A60]">{t.footNote}</p>
        <div className="mt-2 flex items-center justify-center gap-1.5 rounded-full border border-[#2E7D74]/50 py-1.5 text-[9px] font-bold text-[#1D4B57]">
          {t.checkIn} <ArrowRight size={10} />
        </div>
      </div>
    </div>
  );
};

/* Career-journey artwork from the Atikxzio CMH product site; falls back to the HTML replica. */
export const CmhHeroVisual = ({ lang = "en", label = "atikxzio.com/cmh", bare = false }) =>
  CMH_HERO_SHOT ? (
    <div className={bare ? "w-full" : "w-full overflow-hidden rounded-2xl bg-[#F8F6F3] p-2"}>
      <img
        src={CMH_HERO_SHOT}
        alt="Illustration: choosing a career path with guidance and support"
        width="1264"
        height="848"
        loading="lazy"
        decoding="async"
        className={bare ? "w-full" : "w-full rounded-xl"}
      />
    </div>
  ) : (
    <BrowserFrame label={label}><HeroReplica lang={lang} /></BrowserFrame>
  );

/* Phone frame for the "What brings you here today?" step. */
export const CmhIntentVisual = ({ lang = "en", className = "" }) => (
  <div className={`w-[228px] overflow-hidden rounded-[1.6rem] border-[6px] border-[#1D4B57] bg-[#1D4B57] shadow-[0_18px_40px_rgba(29,75,87,0.3)] ${className}`}>
    {CMH_INTENT_SHOT ? (
      <img src={CMH_INTENT_SHOT} alt="Atikxzio CMH — what brings you here today" width="440" height="700" loading="lazy" decoding="async" className="w-full rounded-[1.1rem] bg-white object-cover object-top" />
    ) : (
      <div className="rounded-[1.1rem] bg-white p-1.5"><IntentReplica lang={lang} /></div>
    )}
  </div>
);
